import React from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type JobCardsByBranchChartProps = {
    jobsByBranch: { branch: string; open: number; inProgress: number; closedToday: number }[];
    loading?: boolean;
};

export const JobCardsByBranchChart: React.FC<JobCardsByBranchChartProps> = ({
    jobsByBranch,
    loading
}) => {
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

    const data = jobsByBranch || [];
    const branches = data.map(d => d.branch);
    const openData = data.map(d => d.open);
    const inProgressData = data.map(d => d.inProgress);
    const closedData = data.map(d => d.closedToday);

    const chartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            fontFamily: 'inherit',
            toolbar: {
                show: false
            },
            stacked: true
        },
        plotOptions: {
            bar: {
                horizontal: false,
                borderRadius: 4,
                columnWidth: '60%'
            }
        },
        colors: ['#ffc700', '#009ef7', '#50cd89'], // Yellow (Open), Blue (In Progress), Green (Closed)
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: branches,
            labels: {
                style: {
                    colors: isDarkMode ? '#ffffff' : '#181c32',
                    fontSize: '12px',
                    fontWeight: 600
                },
                rotate: -45,
                rotateAlways: branches.length > 3
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: {
            labels: {
                style: {
                    colors: '#a1a5b7',
                    fontSize: '12px'
                }
            }
        },
        grid: {
            borderColor: isDarkMode ? '#2b2b40' : '#eff2f5',
            strokeDashArray: 4,
            yaxis: {
                lines: {
                    show: true
                }
            }
        },
        tooltip: {
            theme: isDarkMode ? 'dark' : 'light',
            y: {
                formatter: function (val: number) {
                    return val + ' job cards'
                }
            }
        },
        legend: {
            show: true,
            position: 'top',
            horizontalAlign: 'right',
            labels: {
                colors: isDarkMode ? '#ffffff' : '#181c32'
            }
        }
    };

    const series = [
        { name: 'Open', data: openData },
        { name: 'In Progress', data: inProgressData },
        { name: 'Closed Today', data: closedData }
    ];

    return (
        <div className="card h-100" style={{ backgroundColor: isDarkMode ? '#1e1e2d' : '' }}>
            <div className="card-header border-0 pt-5">
                <h3 className="card-title align-items-start flex-column">
                    <span className={`card-label fw-bolder fs-3 ${isDarkMode ? 'text-white' : 'text-dark'}`}>Job Cards by Branch</span>
                    <span className="text-muted mt-1 fw-bold fs-7">Job status breakdown per branch</span>
                </h3>
            </div>
            <div className="card-body pt-3 pb-3">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{height: '300px'}}>
                        <span className="spinner-border text-primary"></span>
                    </div>
                ) : branches.length === 0 ? (
                    <div className="d-flex justify-content-center align-items-center h-200px text-gray-400 fw-bold">
                        No job card data available
                    </div>
                ) : (
                    <div id="jobcards_branch_chart">
                        <Chart options={chartOptions} series={series} type="bar" height={300} />
                    </div>
                )}
            </div>
        </div>
    );
};
